import React, { useState, useEffect } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { 
  BeakerIcon, 
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface CalibrationSession {
  id: string;
  line_id: string;
  mode: string;
  stations_calibrated: string[];
  calibration_data: any;
}

interface CalibrationSimulatorProps {
  lineId: string;
  lineName: string;
  lineNumber: number;
  calibrationStates?: {[key: string]: string};
}

const CalibrationSimulator: React.FC<CalibrationSimulatorProps> = ({ lineId, lineName, lineNumber, calibrationStates }) => {
  const { apiUrl } = useDatabaseContext();
  const [calibrationSession, setCalibrationSession] = useState<CalibrationSession | null>(null);
  const [stationStates, setStationStates] = useState<{[key: string]: string}>({});
  const [isCalibrating, setIsCalibrating] = useState(false);
  
  // Component is rendering
  console.log('CalibrationSimulator mounted for line:', lineNumber, 'lineId:', lineId, 'calibrationStates:', calibrationStates);
  
  // Use passed calibrationStates if available, otherwise fetch them
  useEffect(() => {
    if (calibrationStates && Object.keys(calibrationStates).length > 0) {
      console.log('CalibrationSimulator: Using passed calibration states:', calibrationStates);
      setStationStates(calibrationStates);
      setIsCalibrating(true);
      return; // Don't poll if states are being passed from parent
    }
    
    console.log('CalibrationSimulator useEffect running for lineId:', lineId);
    
    // Run immediately and set up polling
    let intervalId: NodeJS.Timeout;
    
    const checkSession = async () => {
      console.log('CalibrationSimulator checkSession called at', new Date().toISOString());
      try {
        const states: {[key: string]: string} = {};
        let hasCalibration = false;
        
        // First check for calibration session (for infeed)
        const response = await fetch(`${apiUrl}/calibration/session/${lineId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.mode === 'CALIBRATING') {
            setCalibrationSession(data.data);
            setIsCalibrating(true);
            
            // Update infeed state from session
            const calibData = data.data.calibration_data || {};
            if (calibData.infeed) {
              if (calibData.infeed.calibrated) {
                states.infeed = 'calibrated';
              } else if (calibData.infeed.zeroSet) {
                states.infeed = 'zero_set';
              } else {
                states.infeed = 'waiting';
              }
            } else {
              // No calibration data for infeed yet, but session is active, so show as waiting
              states.infeed = 'waiting';
            }
            hasCalibration = true;
          }
        }
        
        // ALWAYS check hardware states for outfeed calibration (don't return early)
        const ioResponse = await fetch(`${apiUrl}/io-system-states`);
        if (ioResponse.ok) {
          const ioData = await ioResponse.json();
          const lineStates = ioData.io_states?.[lineId];
          
          if (lineStates?.outfeed?.devices) {
            // DON'T redeclare states - use the existing one
            
            // Process ALL devices that are RECEIVING (not just the first one)
            const activeDevices = lineStates.outfeed.devices.filter((d: any) => d.mqtt_status === 'RECEIVING');
            console.log('CalibrationSimulator: Found', activeDevices.length, 'active outfeed devices');
            
            // Process each active device
            activeDevices.forEach((device: any) => {
              console.log('CalibrationSimulator: Processing device:', device?.hardware_uuid, 'with stations:', Object.keys(device?.station_states || {}));
              
              if (device?.station_states) {
                // Check each station's calibration status INDEPENDENTLY
                Object.entries(device.station_states).forEach(([stationKey, stationData]: [string, any]) => {
                  const status = stationData?.status || 'STOP';
                  console.log(`CalibrationSimulator: Station ${stationKey} status: ${status}`);
                  
                  // Only add station to states if it's actually in calibration
                  // This ensures each station is independent
                  if (status.includes('CALIBRATION')) {
                    hasCalibration = true;
                    
                    if (status === 'CALIBRATION-COMPLETE') {
                      states[`station_${stationKey}`] = 'calibrated';
                    } else if (status === 'CALIBRATION-ZERO-SET') {
                      states[`station_${stationKey}`] = 'zero_set';
                    } else if (status === 'CALIBRATION-ACTIVE') {
                      states[`station_${stationKey}`] = 'waiting';
                    }
                  }
                  // If station is NOT in calibration, don't add it to states at all
                  // This makes each station completely independent
                });
              }
            });
            
            // Only check infeed status if we didn't already get it from session
            if (!states.infeed) {
              // Check both operational_status and states.line_status for calibration mode
              const infeedStatus = lineStates.infeed?.operational_status || lineStates.infeed?.states?.line_status;
              if (infeedStatus?.includes('CALIBRATION')) {
                hasCalibration = true;
                if (infeedStatus === 'CALIBRATION-COMPLETE') {
                  states.infeed = 'calibrated';
                } else if (infeedStatus === 'CALIBRATION-ZERO-SET') {
                  states.infeed = 'zero_set';
                } else if (infeedStatus === 'CALIBRATION-ACTIVE') {
                  // When in CALIBRATION-ACTIVE, check if we have calibration data to determine actual state
                  // If no calibration data exists yet, it means we're waiting for first button press
                  states.infeed = 'waiting';
                } else {
                  states.infeed = 'waiting';
                }
              }
              // If infeed is NOT in calibration, don't add it to states
              // This keeps infeed and outfeed independent
            }
            
            setIsCalibrating(hasCalibration);
            setStationStates(states);
            console.log('CalibrationSimulator: Updated station states:', states, 'hasCalibration:', hasCalibration);
            
            // Log specific station states for debugging
            if (states.station_1) {
              console.log(`CalibrationSimulator: Station 1 state is '${states.station_1}' - button should show '${states.station_1 === 'zero_set' ? 'Set Span' : states.station_1 === 'waiting' ? 'Set Zero' : 'Complete'}'`);
            }
          } else {
            setCalibrationSession(null);
            setIsCalibrating(false);
            setStationStates({});
          }
        }
      } catch (error) {
        console.error('Error checking calibration session:', error);
      }
    };
    
    // Call immediately
    checkSession();
    
    // Set up polling interval
    intervalId = setInterval(() => {
      checkSession();
    }, 2000);
    
    // Cleanup on unmount
    return () => {
      console.log('CalibrationSimulator cleanup - stopping polling');
      if (intervalId) clearInterval(intervalId);
    };
  }, [apiUrl, lineId, calibrationStates]);
  
  const simulateButtonPress = async (station: string) => {
    console.log(`🔴 CalibrationSimulator: Button pressed for ${station}, current state:`, stationStates[station]);
    console.log(`🔴 Full station states:`, stationStates);
    console.log(`🔴 Line number:`, lineNumber);
    console.log(`🔴 API URL:`, apiUrl);
    
    try {
      // For outfeed stations, we need to send MQTT commands
      if (station.startsWith('station_')) {
        const stationNum = parseInt(station.replace('station_', ''));
        const state = stationStates[station];
        console.log(`🔴 Processing station ${stationNum}, state: ${state}`);
        
        // Get the outfeed hardware UUID for this line
        const hardwareResponse = await fetch(`${apiUrl}/simulated-hardware`);
        if (!hardwareResponse.ok) throw new Error('Failed to fetch hardware');
        
        const hardwareData = await hardwareResponse.json();
        console.log('CalibrationSimulator: Hardware API response:', hardwareData);
        
        // The API returns { data: { devices: [...], ... } }
        let devices = [];
        if (Array.isArray(hardwareData)) {
          devices = hardwareData;
        } else if (hardwareData.data) {
          devices = Array.isArray(hardwareData.data) ? hardwareData.data : (hardwareData.data.devices || []);
        }
        
        console.log('🔴 CalibrationSimulator: Extracted devices:', devices);
        console.log('🔴 CalibrationSimulator: Looking for outfeed device on line', lineNumber, 'for station', stationNum);
        
        // Find the correct outfeed device based on station range
        // Station ranges: 1-4, 5-8, 9-10
        const outfeedDevices = devices.filter((d: any) => 
          d.line_number === lineNumber && d.device_type === 'OUTFEED'
        );
        
        let outfeedDevice = null;
        for (const device of outfeedDevices) {
          // Check if this device handles the target station
          // Device name contains the station range, e.g., "Line1-Outfeed-Sim-Stations1-4"
          const deviceName = device.device_name || '';
          console.log(`🔴 Checking device ${deviceName} for station ${stationNum}`);
          
          if (deviceName.includes('Stations1-4') && stationNum >= 1 && stationNum <= 4) {
            outfeedDevice = device;
            break;
          } else if (deviceName.includes('Stations5-8') && stationNum >= 5 && stationNum <= 8) {
            outfeedDevice = device;
            break;
          } else if (deviceName.includes('Stations9-10') && stationNum >= 9 && stationNum <= 10) {
            outfeedDevice = device;
            break;
          }
        }
        
        if (!outfeedDevice) {
          console.error('🔴 No outfeed device found for line', lineNumber, 'station', stationNum, 'Available devices:', outfeedDevices);
          return;
        }
        console.log('🔴 Found outfeed device:', outfeedDevice.device_name, 'for station', stationNum);
        
        let command, value;
        
        // Determine what command to send based on current state
        if (!state) {
          // Station is NOT in calibration - need to start line-level calibration first
          console.log('🔴 Station not in calibration - checking if line-level calibration is needed');
          
          // Check if ANY station is already in calibration
          const anyStationInCalibration = Object.keys(stationStates).some(key => 
            key.startsWith('station_') && stationStates[key]
          );
          
          if (!anyStationInCalibration) {
            // No stations in calibration yet - start line-level calibration first
            console.log('🔴 No stations in calibration - starting line-level calibration');
            try {
              const response = await fetch(`${apiUrl}/line-modes/${lineId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode: 'calibration' })
              });
              
              if (!response.ok) {
                throw new Error(`Failed to set line mode: ${await response.text()}`);
              }
              
              console.log('✅ Line set to calibration mode - waiting for stations to enter CALIBRATION-ACTIVE');
              // Don't send individual command yet - let the line mode change trigger all stations
              // The component will re-render and show the stations in calibration state
              return;
            } catch (error) {
              console.error('❌ Error setting line to calibration mode:', error);
              return;
            }
          } else {
            // Some stations already in calibration - send individual start command
            console.log('🔴 Other stations already in calibration - sending individual start command');
            command = 'CalibrationMode';
            value = 'START';
          }
          // Don't send SetZero yet - wait for the next button press after state updates
        } else if (state === 'waiting') {
          // Station is in CALIBRATION-ACTIVE state - send SetZero
          command = 'SetZero';
          value = 'ON';
        } else if (state === 'zero_set') {
          // Send SetSpan
          command = 'SetSpan';
          value = 'ON';
        } else {
          return; // Already calibrated
        }
        
        // Send the MQTT command
        if (!command || !value) {
          console.log('🔴 CalibrationSimulator: No command to send for current state:', state);
          return;
        }
        console.log(`🔴 CalibrationSimulator: Sending MQTT command ${command}=${value} for station ${stationNum}, UUID: ${outfeedDevice.hardware_uuid}`);
        const response = await fetch(`${apiUrl}/mqtt/station-command`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hardwareUuid: outfeedDevice.hardware_uuid,
            stationNum,
            command,
            value
          })
        });
        
        if (response.ok) {
          console.log(`✅ Sent ${command} for station ${stationNum}`);
        } else {
          console.error(`❌ Failed to send ${command}:`, await response.text());
        }
      } else {
        // For infeed, use the original calibration/button-press API
        const currentState = stationStates[station];
        let adcValue, weight;
        
        if (!currentState || currentState === 'waiting') {
          // Set Zero - use lower ADC value
          adcValue = 1000000; // 1 million for zero
          weight = 0;
        } else if (currentState === 'zero_set') {
          // Set Span - use higher ADC value (must be different from zero!)
          adcValue = 9000000; // 9 million for span (significantly different from zero)
          weight = 25;
        } else {
          // Already calibrated, shouldn't get here
          console.log('Station already calibrated, skipping button press');
          return;
        }
        
        console.log(`🔴 Infeed calibration: state=${currentState}, ADC=${adcValue}, weight=${weight}`);
        
        const response = await fetch(`${apiUrl}/calibration/button-press`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lineId,
            station,
            adcValue,
            weight
          })
        });
        
        if (response.ok) {
          // State will update via polling
        }
      }
    } catch (error) {
      console.error('Error simulating button press:', error);
    }
  };
  
  const getStateIcon = (state: string | undefined) => {
    if (!state) {
      return <div className="w-5 h-5 rounded-full bg-gray-300" />;
    }
    switch (state) {
      case 'calibrated':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'zero_set':
        return <ArrowPathIcon className="w-5 h-5 text-yellow-600 animate-pulse" />;
      case 'waiting':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-600 animate-pulse" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />;
    }
  };
  
  const getStateText = (state: string | undefined) => {
    if (!state) {
      return 'Not Calibrating';
    }
    switch (state) {
      case 'calibrated':
        return 'Calibrated';
      case 'zero_set':
        return 'Zero Set - Add Weight';
      case 'waiting':
        return 'Press to Zero';
      default:
        return 'Ready';
    }
  };
  
  const getButtonText = (station: string) => {
    const state = stationStates[station];
    if (!state && station.startsWith('station_')) {
      // Check if any other stations are in calibration
      const anyStationInCalibration = Object.keys(stationStates).some(key => 
        key.startsWith('station_') && stationStates[key]
      );
      
      if (!anyStationInCalibration) {
        return '🚀 Start Line Calibration';  // First station clicked starts entire line
      } else {
        return '▶️ Join Calibration';  // Other stations join existing calibration
      }
    } else if (state === 'calibrated') {
      return '✅ Complete';
    } else if (state === 'zero_set') {
      return '⚖️ Set Span (25 lbs)';
    } else if (state === 'waiting') {
      return '🎯 Set Zero';
    } else {
      return '🎯 Set Zero';
    }
  };
  
  console.log('CalibrationSimulator render - isCalibrating:', isCalibrating, 'stationStates:', stationStates);
  console.log('CalibrationSimulator render - lineNumber:', lineNumber, 'apiUrl:', apiUrl);
  
  // Always show the calibration simulator on the Hardware Simulation page
  // The page already controls when to show it based on line calibration status
  // if (!isCalibrating) {
  //   return null; // Don't show anything if not in calibration mode
  // }
  
  return (
    <div className="bg-white shadow-sm rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 bg-yellow-50">
        <div className="flex items-center space-x-3">
          <BeakerIcon className="w-6 h-6 text-yellow-600 animate-pulse" />
          <div>
            <h3 className="text-lg font-medium text-slate-900">Calibration Mode Active</h3>
            <p className="text-sm text-slate-600">
              Line {lineNumber} is in calibration mode. Use the buttons below to simulate hardware button presses.
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {/* Infeed Calibration - Only show if infeed is in calibration */}
        {stationStates.infeed && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Infeed Scale</h4>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStateIcon(stationStates.infeed)}
                  <div>
                    <p className="font-medium text-slate-900">Infeed Hopper</p>
                    <p className="text-sm text-slate-600">{getStateText(stationStates.infeed)}</p>
                  </div>
                </div>
                <button
                  onClick={() => simulateButtonPress('infeed')}
                  disabled={stationStates.infeed === 'calibrated'}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    stationStates.infeed === 'calibrated'
                      ? 'bg-green-100 text-green-800 cursor-not-allowed'
                      : stationStates.infeed === 'zero_set'
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {getButtonText('infeed')}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Outfeed Stations - Show all stations when in calibration mode */}
        {(
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Outfeed Stations</h4>
            <div className="grid grid-cols-2 gap-4">
            {/* Left column - Stations 1-5 */}
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(num => {
                const station = `station_${num}`;
                // Always render stations, but show different UI based on calibration state
                return (
                  <div key={station} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getStateIcon(stationStates[station])}
                        <div>
                          <p className="font-medium text-slate-900 text-sm">Station {num}</p>
                          <p className="text-xs text-slate-600">{getStateText(stationStates[station])}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(`🔵 Button clicked for station ${station}, disabled:`, stationStates[station] === 'calibrated');
                          console.log(`🔵 Station state:`, stationStates[station]);
                          simulateButtonPress(station);
                        }}
                        disabled={false} // Temporarily disable the disabled check to test
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                          stationStates[station] === 'calibrated'
                            ? 'bg-green-100 text-green-800 cursor-not-allowed'
                            : stationStates[station] === 'zero_set'
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {getButtonText(station)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Right column - Stations 6-10 */}
            <div className="space-y-3">
              {[6, 7, 8, 9, 10].map(num => {
                const station = `station_${num}`;
                // Always render stations, but show different UI based on calibration state
                return (
                  <div key={station} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getStateIcon(stationStates[station])}
                        <div>
                          <p className="font-medium text-slate-900 text-sm">Station {num}</p>
                          <p className="text-xs text-slate-600">{getStateText(stationStates[station])}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(`🔵 Button clicked for station ${station}, disabled:`, stationStates[station] === 'calibrated');
                          console.log(`🔵 Station state:`, stationStates[station]);
                          simulateButtonPress(station);
                        }}
                        disabled={false} // Temporarily disable the disabled check to test
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                          stationStates[station] === 'calibrated'
                            ? 'bg-green-100 text-green-800 cursor-not-allowed'
                            : stationStates[station] === 'zero_set'
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {getButtonText(station)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}
        
        {/* Progress Summary */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-slate-600">
                Calibration Progress: {Object.values(stationStates).filter(s => s === 'calibrated').length} of 11 stations complete
              </p>
              <div className="mt-2 w-64 bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(Object.values(stationStates).filter(s => s === 'calibrated').length / 11) * 100}%` }}
                />
              </div>
            </div>
            {Object.values(stationStates).filter(s => s === 'calibrated').length === 11 && (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircleIcon className="w-5 h-5" />
                  <span className="font-medium">All stations calibrated!</span>
                </div>
                <button
                  onClick={async () => {
                    console.log('🔴 Complete Calibration button pressed - changing line mode to STOP');
                    try {
                      // Change the line mode to STOP which will send MQTT NCMD message
                      const response = await fetch(`${apiUrl}/line-modes/${lineId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mode: 'stop' })
                      });
                      
                      if (response.ok) {
                        console.log('✅ Successfully changed line mode to STOP');
                      } else {
                        console.error('❌ Failed to change line mode:', await response.text());
                      }
                    } catch (error) {
                      console.error('❌ Error changing line mode:', error);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
                >
                  Complete Calibration
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalibrationSimulator;